// src/components/POSFileImport.jsx
// Drag a POS export (CSV/XLSX) -> receipts + item_statuses created -> confirmed SMS send.
// Deps: npm i papaparse xlsx   ·   Uses existing supabase client + /api/sms/send-receipt.
//
// Guarantees enforced client-side:
//   - Parsing is deterministic; AI is never in the write path.
//   - Send button only targets receipts CREATED in this batch (never updates/dupes).
//   - Send is a separate, human-confirmed step with TCPA attestation checkbox.

import { useState, useCallback, useMemo } from 'react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { supabase, authHeaders } from '../supabaseClient';
import presetsFile from '../config/importPresets.json';

const CANONICAL = [
  ['external_ref', 'Order / Invoice # *'],
  ['customer_name', 'Customer Name *'],
  ['customer_phone', 'Customer Phone *'],
  ['customer_email', 'Email'],
  ['delivery_address', 'Delivery Address'],
  ['delivery_date', 'Delivery Date'],
  ['delivery_instructions', 'Delivery Instructions'],
  ['salesperson', 'Salesperson'],
  ['order_date', 'Order Date'],
  ['subtotal', 'Subtotal'],
  ['tax', 'Tax'],
  ['total', 'Total'],
  ['amount_paid', 'Amount Paid / Deposits'],
  ['item_status_raw', 'Item Status'],
  ['item_sku', 'Item SKU / Model'],
  ['item_name', 'Item Description'],
  ['item_qty', 'Item Qty'],
  ['item_price', 'Item Price'],
  ['item_color', 'Item Color'],
];
// Mirrors statusConstants.js / status_constants.py (hand-sync rule).
// 'awaiting' = item not yet arrived → importer creates NO item_statuses row.
const ITEM_STATUS_OPTIONS = ['at_store', 'out_for_delivery', 'delivered', 'picked_up', 'awaiting'];

const fingerprint = (headers) =>
  [...headers].map((h) => (h || '').toLowerCase().trim()).sort().join('|');

async function parseFile(file) {
  const isSheet = /\.(xlsx|xls)$/i.test(file.name);
  if (isSheet) {
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: 'array' });
    const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '', raw: false });
    return { headers: rows.length ? Object.keys(rows[0]) : [], rows };
  }
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true, skipEmptyLines: 'greedy',
      complete: (r) => resolve({ headers: r.meta.fields || [], rows: r.data }),
      error: reject,
    });
  });
}

// simple header auto-guess for files with no matching template
const GUESSES = {
  external_ref: /order\s*(no|#|num)|invoice\s*#?/i, customer_name: /customer\s*name|^name$/i,
  customer_phone: /cell|mobile|^phone/i, customer_email: /e-?mail/i,
  delivery_address: /deliver.*addr|address/i, delivery_date: /deliv.*date|sched.*date/i,
  delivery_instructions: /instruction|note/i, salesperson: /sales(person|man)?/i,
  order_date: /order\s*date|invoice\s*date/i, subtotal: /merch|subtotal/i, tax: /^tax/i,
  total: /order\s*total|^total|ext\s*total/i, amount_paid: /paid|deposit/i,
  item_status_raw: /status/i, item_sku: /sku|model|product/i, item_name: /desc/i,
  item_qty: /qty|quant/i, item_price: /unit\s*price|^price/i, item_color: /color/i,
};
const autoGuess = (headers) => {
  const m = {};
  for (const [field, rx] of Object.entries(GUESSES)) {
    const hit = headers.find((h) => rx.test(h));
    if (hit && !Object.values(m).includes(hit)) m[field] = hit;
  }
  return m;
};

export default function POSFileImport({ currentStore, onClose, onImported }) {
  const [step, setStep] = useState('drop'); // drop | map | preview | done
  const [primary, setPrimary] = useState(null);     // {headers, rows, name}
  const [secondary, setSecondary] = useState(null); // optional line-items file (STORIS Detail)
  const [preset, setPreset] = useState(null);
  const [mapping, setMapping] = useState({});
  const [secondaryMapping, setSecondaryMapping] = useState({});
  const [statusMap, setStatusMap] = useState({});
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [consentAttested, setConsentAttested] = useState(false);
  const [sendProgress, setSendProgress] = useState(null); // {sent, total}
  const [saveTemplateName, setSaveTemplateName] = useState('');
  const [usedTemplateName, setUsedTemplateName] = useState(null);

  const distinctStatuses = useMemo(() => {
    if (!primary) return [];
    const col = mapping.item_status_raw;
    const col2 = secondaryMapping.item_status_raw;
    const vals = new Set();
    if (col) primary.rows.forEach((r) => r[col] && vals.add(String(r[col]).trim()));
    if (col2 && secondary) secondary.rows.forEach((r) => r[col2] && vals.add(String(r[col2]).trim()));
    return [...vals].slice(0, 40);
  }, [primary, secondary, mapping.item_status_raw, secondaryMapping.item_status_raw]);

  const handleDrop = useCallback(async (file, slot = 'primary') => {
    setError(null);
    try {
      const parsed = await parseFile(file);
      if (!parsed.rows.length) throw new Error('No data rows found in file');
      const data = { ...parsed, name: file.name };
      if (slot === 'secondary') { setSecondary(data); return; }
      setPrimary(data);

      // 1) saved store templates by fingerprint, 2) global presets, 3) auto-guess
      const fp = fingerprint(parsed.headers);
      const { data: tmpls } = await supabase
        .from('import_templates').select('*')
        .or(`store_id.eq.${currentStore?.id},store_id.is.null`);
      setSavedTemplates(tmpls || []);
      const exact = (tmpls || []).find((t) => t.header_fingerprint === fp);
      if (exact) {
        // Known format: skip the mapping wall entirely — straight to preview.
        setPreset(exact); setMapping(exact.mapping || {});
        setStatusMap(exact.status_map || {}); setSecondaryMapping(exact.secondary_mapping || {});
        setUsedTemplateName(exact.name || 'saved mapping');
        await runImport(true, {
          rows: parsed.rows,
          mapping: exact.mapping || {},
          statusMap: exact.status_map || {},
          source: exact.source_system,
          templateId: exact.id,
          filename: file.name,
        });
        return;
      }
      setUsedTemplateName(null);
      {
        const guess = presetsFile.presets.find((p) =>
          Object.values(p.mapping).filter((h) => parsed.headers.includes(h)).length >=
          Object.values(p.mapping).length * 0.7);
        if (guess) {
          setPreset(guess); setMapping(guess.mapping);
          setStatusMap(guess.status_map || {}); setSecondaryMapping(guess.secondary_mapping || {});
        } else {
          setPreset(null); setMapping(autoGuess(parsed.headers)); setStatusMap({});
        }
      }
      setStep('map');
    } catch (e) { setError(e.message); }
  }, [currentStore?.id]);

  const runImport = async (dryRun, o = {}) => {
    setBusy(true); setError(null);
    try {
      const res = await fetch('/api/import-orders', {
        method: 'POST', headers: await authHeaders(),
        body: JSON.stringify({
          store_id: currentStore?.id,
          rows: o.rows ?? primary.rows,
          mapping: o.mapping ?? mapping,
          secondary_rows: secondary?.rows || null,
          secondary_mapping: secondary ? secondaryMapping : null,
          status_map: o.statusMap ?? statusMap,
          source_system: o.source ?? (preset?.source_system || 'generic_csv'),
          filename: o.filename ?? primary?.name,
          template_id: o.templateId ?? (preset?.id || null),
          sms_consent_attested: consentAttested,
          dry_run: dryRun,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Import failed');
      setResult(data);
      setStep(dryRun ? 'preview' : 'done');
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const saveTemplate = async () => {
    if (!saveTemplateName.trim()) return;
    await supabase.from('import_templates').insert({
      store_id: currentStore?.id, name: saveTemplateName.trim(),
      source_system: preset?.source_system || 'generic_csv',
      header_fingerprint: fingerprint(primary.headers),
      mapping, status_map: statusMap,
      secondary_mapping: secondary ? secondaryMapping : null,
      secondary_fingerprint: secondary ? fingerprint(secondary.headers) : null,
    });
    setSaveTemplateName('');
  };

  // Confirmed send — CREATED receipts only, via existing endpoint
  const sendReceipts = async () => {
    const ids = result?.created_receipt_ids || [];
    setSendProgress({ sent: 0, total: ids.length });
    let sent = 0;
    for (const receiptId of ids) {
      try {
        const { data: r } = await supabase.from('receipts')
          .select('customer_phone, customer_name').eq('id', receiptId).single();
        if (!r?.customer_phone) continue;
        const resp = await fetch('/api/sms/send-receipt', {
          method: 'POST', headers: await authHeaders(),
          body: JSON.stringify({
            phoneNumber: r.customer_phone, receiptId,
            customerName: r.customer_name || 'Customer',
            businessName: currentStore?.name || null,
          }),
        });
        if (resp.ok) sent += 1;
      } catch { /* continue; per-receipt failures don't halt the batch */ }
      setSendProgress({ sent, total: ids.length });
      await new Promise((r) => setTimeout(r, 350)); // pacing; swap for drip engine when live
    }
    await supabase.from('import_batches').update({ sms_sent_count: sent }).eq('id', result.batch_id);
    setSendProgress({ sent, total: ids.length, finished: true });
  };

  const Field = ({ f, label, map, setMap, headers }) => (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="text-sm text-slate-600 min-w-0 truncate">{label}</span>
      <select
        className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 bg-white max-w-[220px]"
        value={map[f] || ''}
        onChange={(e) => setMap((m) => ({ ...m, [f]: e.target.value || undefined }))}
      >
        <option value="">— not in file —</option>
        {headers.map((h) => <option key={h} value={h}>{h}</option>)}
      </select>
    </div>
  );

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-xl p-5 max-w-3xl w-full flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-slate-900">Import POS File</h2>
        {onClose && <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-sm">Close</button>}
      </div>

      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}

      {step === 'drop' && (
        <label className="border-2 border-dashed border-slate-300 rounded-xl p-10 text-center cursor-pointer hover:border-slate-400 transition-colors">
          <input type="file" accept=".csv,.xlsx,.xls" className="hidden"
            onChange={(e) => e.target.files?.[0] && handleDrop(e.target.files[0])} />
          <p className="font-medium text-slate-900 text-sm">Drop your POS export here</p>
          <p className="text-xs text-slate-400 mt-1">STORIS · Furniture Wizard · any CSV/Excel — we'll recognize it</p>
        </label>
      )}

      {step === 'map' && primary && (
        <div className="flex flex-col gap-4">
          <div className="text-xs text-slate-400">
            {primary.name} · {primary.rows.length} rows
            {preset && <span className="ml-2 text-emerald-600 font-medium">✓ Recognized: {preset.name}</span>}
          </div>

          <div className="grid sm:grid-cols-2 gap-x-8">
            {CANONICAL.map(([f, label]) => (
              <Field key={f} f={f} label={label} map={mapping} setMap={setMapping} headers={primary.headers} />
            ))}
          </div>

          {/* Optional line-items file (STORIS Detail) */}
          <div className="border-t border-slate-100 pt-3">
            {!secondary ? (
              <label className="text-xs text-slate-500 cursor-pointer hover:text-slate-700">
                + Add line-items file (optional — e.g. STORIS Open Sales Order Detail)
                <input type="file" accept=".csv,.xlsx,.xls" className="hidden"
                  onChange={(e) => e.target.files?.[0] && handleDrop(e.target.files[0], 'secondary')} />
              </label>
            ) : (
              <div className="text-xs text-slate-500">
                Line items: <span className="font-medium text-slate-700">{secondary.name}</span> · {secondary.rows.length} rows
                <div className="grid sm:grid-cols-2 gap-x-8 mt-2">
                  {CANONICAL.filter(([f]) => f === 'external_ref' || f.startsWith('item_')).map(([f, label]) => (
                    <Field key={f} f={f} label={label} map={secondaryMapping} setMap={setSecondaryMapping} headers={secondary.headers} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {distinctStatuses.length > 0 && (
            <div className="border-t border-slate-100 pt-3">
              <p className="text-sm font-medium text-slate-900 mb-2">Status mapping</p>
              <div className="grid sm:grid-cols-2 gap-x-8">
                {distinctStatuses.map((s) => (
                  <div key={s} className="flex items-center justify-between gap-3 py-1">
                    <span className="text-sm text-slate-600 truncate">"{s}"</span>
                    <select className="text-sm border border-slate-200 rounded-lg px-2 py-1 bg-white"
                      value={statusMap[s] || 'at_store'}
                      onChange={(e) => setStatusMap((m) => ({ ...m, [s]: e.target.value }))}>
                      {ITEM_STATUS_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-end gap-2">
            <button onClick={() => runImport(true)} disabled={busy}
              className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium disabled:opacity-50">
              {busy ? 'Checking…' : 'Preview import'}
            </button>
          </div>
        </div>
      )}

      {step === 'preview' && result && (
        <div className="flex flex-col gap-3">
          {usedTemplateName && (
            <div className="text-xs text-slate-400">
              {primary?.name} · using saved mapping "{usedTemplateName}" ·{' '}
              <button onClick={() => setStep('map')} className="underline hover:text-slate-600">Edit mapping</button>
            </div>
          )}
          <div className="flex items-center gap-4 text-sm">
            <span className="text-emerald-600 font-medium">{result.created} new receipts</span>
            <span className="text-slate-500">{result.updated} status updates</span>
            <span className={result.skipped_count ? 'text-amber-600' : 'text-slate-400'}>{result.skipped_count} skipped</span>
          </div>
          {result.skipped?.length > 0 && (
            <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
              {result.skipped.map((s, i) => (
                <div key={i} className="px-3 py-1.5 text-xs text-slate-500">
                  <span className="font-medium text-slate-700">{s.ref || `row ${s.row}`}</span> — {s.reason}
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2">
            <input value={saveTemplateName} onChange={(e) => setSaveTemplateName(e.target.value)}
              placeholder="Save mapping as… (e.g. Nightly STORIS)"
              className="flex-1 text-sm border border-slate-200 rounded-lg px-3 py-2" />
            <button onClick={saveTemplate} className="text-sm text-slate-600 hover:text-slate-900">Save</button>
          </div>
          <div className="flex items-center justify-end gap-2">
            <button onClick={() => setStep('map')} className="px-4 py-2 rounded-lg text-sm text-slate-600">Back</button>
            <button onClick={() => runImport(false)} disabled={busy}
              className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium disabled:opacity-50">
              {busy ? 'Importing…' : `Import ${result.created} receipts`}
            </button>
          </div>
        </div>
      )}

      {step === 'done' && result && (
        <div className="flex flex-col gap-4">
          <div className="text-sm">
            <span className="text-emerald-600 font-medium">✓ {result.created} receipts created</span>
            <span className="text-slate-500 ml-3">{result.updated} updated · {result.skipped_count} skipped</span>
          </div>
          {result.created > 0 && !sendProgress && (
            <div className="border border-slate-200 rounded-xl p-4 flex flex-col gap-3">
              <label className="flex items-start gap-2 text-xs text-slate-500">
                <input type="checkbox" checked={consentAttested}
                  onChange={(e) => setConsentAttested(e.target.checked)} className="mt-0.5" />
                <span>These customers provided their phone number at purchase and consented to
                order/delivery text messages (recorded on receipts for TCPA compliance).</span>
              </label>
              <button onClick={sendReceipts} disabled={!consentAttested}
                className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium disabled:opacity-40">
                Send {result.created} SMS receipts
              </button>
              <p className="text-[11px] text-slate-400">Only receipts created in this import are texted. Updated orders are never re-sent.</p>
            </div>
          )}
          {sendProgress && (
            <div className="text-sm text-slate-600">
              {sendProgress.finished
                ? <span className="text-emerald-600 font-medium">✓ {sendProgress.sent}/{sendProgress.total} texts sent — the AI agents are live on these orders.</span>
                : <>Sending… {sendProgress.sent}/{sendProgress.total}</>}
            </div>
          )}
          <div className="flex justify-end">
            <button onClick={() => { onImported?.(); onClose?.(); }}
              className="px-4 py-2 rounded-lg text-sm text-slate-600 hover:text-slate-900">Done</button>
          </div>
        </div>
      )}
    </div>
  );
}